import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../api/api.service';

export enum NotificationCategory {
  SYSTEM = 'system',
  TRADING = 'trading',
  ACCOUNT = 'account',
  SECURITY = 'security',
  SUPPORT = 'support',
  DOCUMENTS = 'documents',
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface NotificationCreate {
  title: string;
  details?: string;
  priority: NotificationPriority;
  category: NotificationCategory;
  action_required: boolean;
  user_id?: number;
  author_id?: number;
  author_name?: string;
}

export interface NotificationResponse {
  id: number;
  title: string;
  details?: string;
  priority: string; // API returns strings like "High Priority", "Low Priority", etc.
  category: string; // API returns strings like "Trading", "System", etc.
  action_required: boolean;
  is_read: boolean;
  user_id: number;
  author_id?: number;
  author_name?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationsListResponse {
  notifications: NotificationResponse[];
  total: number;
  unread_count: number;
  alerts: number;
  messages: number;
}

export interface UnreadCountResponse {
  count: number;
}

export interface MarkAllReadResponse {
  message: string;
  count: number;
}

export interface NotificationQueryParams {
  is_read?: boolean;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  limit?: number;
  offset?: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(private apiService: ApiService) {}

  /**
   * Get notifications for the current user
   */
  getNotifications(
    params?: NotificationQueryParams,
    skipGlobalLoader = false,
  ): Observable<NotificationsListResponse> {
    return this.apiService.get<NotificationsListResponse>('/notifications', params, true, skipGlobalLoader);
  }

  /**
   * Get a specific notification by ID
   */
  getNotificationById(notificationId: number, skipGlobalLoader = false): Observable<NotificationResponse> {
    return this.apiService.get<NotificationResponse>(
      `/notifications/${notificationId}`,
      undefined,
      true,
      skipGlobalLoader,
    );
  }

  /**
   * Create a new notification (admin only)
   */
  createNotification(notification: NotificationCreate): Observable<NotificationResponse> {
    return this.apiService.post<NotificationResponse>('/notifications', notification);
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: number, skipGlobalLoader = false): Observable<NotificationResponse> {
    return this.apiService.patch<NotificationResponse>(
      `/notifications/${notificationId}/read`,
      {},
      true,
      skipGlobalLoader,
    );
  }

  /**
   * Mark all notifications as read for the current user
   */
  markAllAsRead(): Observable<MarkAllReadResponse> {
    return this.apiService.post<MarkAllReadResponse>('/notifications/mark-all-read', {});
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: number): Observable<void> {
    return this.apiService.delete<void>(`/notifications/${notificationId}`);
  }

  /**
   * Get unread notification count for the current user
   */
  getUnreadCount(skipGlobalLoader = false): Observable<number> {
    return this.apiService
      .get<UnreadCountResponse>('/notifications/unread/counts', undefined, true, skipGlobalLoader)
      .pipe(map((response) => response.count));
  }
}

